
import { defineComponent, ref, computed, onMounted, toRefs, watch } from "vue"
import { useStore } from "vuex"
import { Actions } from "@/store/enums/StoreEnums"
import { ElMessage, ElMessageBox } from "element-plus"
import type { ElForm } from "element-plus"
import ApiService from "@/core/services/ApiService"

type FormInstance = InstanceType<typeof ElForm>;

interface Product {
  id: number;
  name: string;
  image: string | null;
  recycle_price: number;
}

interface MatchPoolItem {
  id?: number;
  match_pool_id?: number;
  product_id: number;
  product?: Product;
  quantity: number;
  sort_order: number;
}

interface ItemForm {
  product_id: number | null;
  quantity: number;
  sort_order: number;
}

export default defineComponent({
  props: {
    matchPoolId: {
      type: Number,
      required: true
    },
    defaultQuantity: {
      type: Number,
      default: 9
    }
  },
  emits: ['update'],
  setup(props, { emit }) {
    const store = useStore()
    const { matchPoolId, defaultQuantity } = toRefs(props)

    const items = ref<MatchPoolItem[]>([])
    const dialogVisible = ref(false)
    const isEditingItem = ref(false)
    const editingItemId = ref<number | null>(null)
    const itemFormRef = ref<FormInstance>()

    const productOptions = ref<Product[]>([])
    const productSearchLoading = ref(false)

    const itemForm = ref<ItemForm>({
      product_id: null,
      quantity: 1,
      sort_order: 0
    })

    const itemRules = ref({
      product_id: [
        { required: true, message: '请选择商品', trigger: 'change' }
      ],
      quantity: [
        { required: true, message: '请输入数量', trigger: 'blur' }
      ]
    })

    // 按排序显示
    const sortedItems = computed(() => {
      return [...items.value].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    })

    const loadItems = async () => {
      try {
        ApiService.setHeader()
        const { data } = await ApiService.get(`admin/match_pools/${matchPoolId.value}`)
        items.value = data.match_pool?.match_pool_items || []
      } catch (error) {
        ElMessage.error('加载赏品列表失败')
      }
    }

    const searchProducts = async (query: string) => {
      if (!query || query.length < 1) {
        productOptions.value = []
        return
      }

      productSearchLoading.value = true
      try {
        ApiService.setHeader()
        const { data } = await ApiService.get(`admin/products?name=${encodeURIComponent(query)}&pageSize=20`)
        productOptions.value = data.products || []
      } catch (error) {
        ElMessage.error('搜索商品失败')
      } finally {
        productSearchLoading.value = false
      }
    }

    const onAddItem = () => {
      isEditingItem.value = false
      editingItemId.value = null
      itemForm.value = {
        product_id: null,
        quantity: defaultQuantity.value,
        sort_order: items.value.length
      }
      productOptions.value = []
      dialogVisible.value = true
    }

    const onEditItem = (row: MatchPoolItem) => {
      isEditingItem.value = true
      editingItemId.value = row.id || null
      itemForm.value = {
        product_id: row.product_id,
        quantity: row.quantity,
        sort_order: row.sort_order
      }
      // 预填充商品选项
      if (row.product) {
        productOptions.value = [row.product]
      }
      dialogVisible.value = true
    }

    const onSaveItem = async () => {
      if (!itemFormRef.value) return

      try {
        await itemFormRef.value.validate()
      } catch {
        return
      }

      try {
        ApiService.setHeader()

        const payload = {
          match_pool_item: {
            product_id: itemForm.value.product_id,
            quantity: itemForm.value.quantity,
            sort_order: itemForm.value.sort_order
          }
        }

        if (isEditingItem.value && editingItemId.value) {
          // 更新
          await ApiService.put(
            `admin/match_pools/${matchPoolId.value}/match_pool_items/${editingItemId.value}`,
            payload
          )
          ElMessage.success('更新成功')
        } else {
          // 创建
          await ApiService.post(
            `admin/match_pools/${matchPoolId.value}/match_pool_items`,
            payload
          )
          ElMessage.success('添加成功')
        }

        dialogVisible.value = false
        await loadItems()
        emit('update')
      } catch (error: unknown) {
        const errorMsg = (error as { response?: { data?: { error_msg?: string } } })?.response?.data?.error_msg || '操作失败'
        ElMessage.error(errorMsg)
      }
    }

    const onDeleteItem = (row: MatchPoolItem) => {
      ElMessageBox.confirm(
        `确定要删除赏品「${row.product?.name || '未知'}」吗？`,
        '删除确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
        .then(async () => {
          try {
            ApiService.setHeader()
            await ApiService.delete(
              `admin/match_pools/${matchPoolId.value}/match_pool_items/${row.id}`
            )
            ElMessage.success('删除成功')
            await loadItems()
            emit('update')
          } catch (error) {
            ElMessage.error('删除失败')
          }
        })
        .catch(() => {
          // 取消删除
        })
    }

    onMounted(() => {
      loadItems()
    })

    watch(matchPoolId, () => {
      loadItems()
    })

    return {
      items,
      sortedItems,
      dialogVisible,
      isEditingItem,
      itemFormRef,
      itemForm,
      itemRules,
      productOptions,
      productSearchLoading,
      searchProducts,
      onAddItem,
      onEditItem,
      onSaveItem,
      onDeleteItem
    }
  }
})
